/**
 * StandalonePaymentResult Page
 *
 * Displays payment result after WebPay redirect for standalone payment links.
 *
 * Route: /pago/link/resultado
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ArrowLeft,
  Loader2,
  Calendar,
  MapPin,
  Mail,
  Phone
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { confirmStandalonePayment, parseWebPayParams, getPaymentResultType } from '../services/standalonePaymentApi';
import { formatCLP } from '../utils/priceFormat';

export default function StandalonePaymentResult() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function processPayment() {
      try {
        const params = parseWebPayParams();
        const resultType = getPaymentResultType(params);

        if (resultType === 'unknown') {
          setError('No se encontraron parámetros de pago válidos.');
          setIsLoading(false);
          return;
        }

        const confirmResult = await confirmStandalonePayment(params);
        setResult(confirmResult);
      } catch (err) {
        console.error('Payment confirmation error:', err);
        setError(err.message || 'Error al confirmar el pago.');
      } finally {
        setIsLoading(false);
      }
    }

    processPayment();
  }, []);

  const handleGoHome = () => navigate('/');
  const handleViewCourses = () => navigate('/cursos');

  return (
    <>
      <Header />
      <section className="min-h-screen py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-cyan-500/20 via-transparent to-transparent" />
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/20">

              {/* Loading */}
              {isLoading && (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <Loader2 className="w-10 h-10 text-white animate-spin" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">Verificando Pago</h2>
                  <p className="text-slate-400">Estamos confirmando tu pago con WebPay. Por favor espera...</p>
                </motion.div>
              )}

              {/* Error (no result) */}
              {!isLoading && error && !result && (
                <ResultCard
                  icon={<AlertCircle className="w-10 h-10 text-white" />}
                  iconBg="from-red-500 to-rose-500"
                  iconShadow="shadow-red-500/30"
                  title="Error"
                  message={error}
                  footer={<p className="text-sm text-slate-500">Si el problema persiste, contacta al Instituto Valparaíso.</p>}
                  onGoHome={handleGoHome}
                />
              )}

              {/* Success */}
              {!isLoading && result?.status === 'approved' && (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">¡Pago Exitoso!</h2>
                  <p className="text-slate-400 mb-8">Tu inscripción al curso presencial ha sido confirmada.</p>

                  {result.transactionDetails && (
                    <div className="bg-white/5 rounded-2xl p-6 mb-6 text-left">
                      <h3 className="text-lg font-semibold text-white mb-4">Detalles del Pago</h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Orden de compra:</span>
                          <span className="text-white font-mono">{result.buyOrder}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Código de autorización:</span>
                          <span className="text-white font-mono">{result.transactionDetails.authorizationCode}</span>
                        </div>
                        {result.transactionDetails.cardNumber && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Tarjeta:</span>
                            <span className="text-white">**** {result.transactionDetails.cardNumber}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-slate-400">Monto:</span>
                          <span className="text-white font-semibold">{formatCLP(result.transactionDetails.amount)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {result.courseDetails && (
                    <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-6 mb-6 text-left border border-blue-400/20">
                      <h3 className="text-lg font-semibold text-white mb-4">Información del Curso</h3>
                      <div className="space-y-3">
                        <p className="text-cyan-300 font-medium">{result.courseDetails.courseName}</p>
                        {result.courseDetails.courseDate && (
                          <div className="flex items-center gap-2 text-slate-300 text-sm">
                            <Calendar className="w-4 h-4 text-cyan-400" />
                            <span>Fecha: {result.courseDetails.courseDate}</span>
                          </div>
                        )}
                        {result.courseDetails.courseLocation && (
                          <div className="flex items-center gap-2 text-slate-300 text-sm">
                            <MapPin className="w-4 h-4 text-cyan-400" />
                            <span>Lugar: {result.courseDetails.courseLocation}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-white/5 rounded-2xl p-6 mb-6 text-left">
                    <h3 className="text-lg font-semibold text-white mb-4">Próximos Pasos</h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                      <li className="flex items-start gap-2">
                        <Mail className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>Recibirás un email de confirmación con todos los detalles.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Phone className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>Nos pondremos en contacto contigo antes del inicio del curso.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>Recuerda traer tu documento de identidad el día del curso.</span>
                      </li>
                    </ul>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      onClick={handleViewCourses}
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all"
                    >
                      Ver Más Cursos
                    </motion.button>
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      onClick={handleGoHome}
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
                    >
                      <ArrowLeft className="w-5 h-5" /> Volver al Inicio
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {/* Cancelled */}
              {!isLoading && result?.status === 'cancelled' && (
                <ResultCard
                  icon={<XCircle className="w-10 h-10 text-white" />}
                  iconBg="from-amber-500 to-orange-500"
                  iconShadow="shadow-amber-500/30"
                  title="Pago Cancelado"
                  message="Has cancelado el proceso de pago. No se ha realizado ningún cargo."
                  buyOrder={result.buyOrder}
                  footer={<p className="text-sm text-slate-300">Puedes solicitar un nuevo enlace de pago al instituto.</p>}
                  onGoHome={handleGoHome}
                />
              )}

              {/* Timeout */}
              {!isLoading && result?.status === 'timeout' && (
                <ResultCard
                  icon={<Clock className="w-10 h-10 text-white" />}
                  iconBg="from-amber-500 to-orange-500"
                  iconShadow="shadow-amber-500/30"
                  title="Tiempo Agotado"
                  message="El tiempo para completar el pago ha expirado. No se ha realizado ningún cargo."
                  buyOrder={result.buyOrder}
                  footer={<p className="text-sm text-slate-300">Puedes solicitar un nuevo enlace de pago al instituto.</p>}
                  onGoHome={handleGoHome}
                />
              )}

              {/* Rejected */}
              {!isLoading && result?.status === 'rejected' && (
                <ResultCard
                  icon={<XCircle className="w-10 h-10 text-white" />}
                  iconBg="from-red-500 to-rose-500"
                  iconShadow="shadow-red-500/30"
                  title="Pago Rechazado"
                  message={result.rejectionReason || result.errorMessage || 'Tu pago no pudo ser procesado.'}
                  buyOrder={result.buyOrder}
                  footer={<p className="text-sm text-slate-300">Verifica los datos de tu tarjeta o intenta con otro medio de pago.</p>}
                  onGoHome={handleGoHome}
                />
              )}

              {/* Generic error from result */}
              {!isLoading && result?.status === 'error' && (
                <ResultCard
                  icon={<AlertCircle className="w-10 h-10 text-white" />}
                  iconBg="from-red-500 to-rose-500"
                  iconShadow="shadow-red-500/30"
                  title="Error"
                  message={result.errorMessage || 'Error desconocido'}
                  footer={<p className="text-sm text-slate-300">Si el problema persiste, contacta al Instituto Valparaíso.</p>}
                  onGoHome={handleGoHome}
                />
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

/**
 * Reusable result card for non-success states
 */
function ResultCard({ icon, iconBg, iconShadow, title, message, buyOrder, footer, onGoHome }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
      <div className={`w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br ${iconBg} flex items-center justify-center shadow-lg ${iconShadow}`}>
        {icon}
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{title}</h2>
      <p className="text-slate-400 mb-4 max-w-md mx-auto">{message}</p>
      {buyOrder && <p className="text-sm text-slate-500 mb-6">Referencia: {buyOrder}</p>}
      {footer && <div className="bg-white/5 rounded-xl p-4 mb-8">{footer}</div>}
      <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
        onClick={onGoHome}
        className="flex items-center justify-center gap-2 px-6 py-3 mx-auto bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
      >
        <ArrowLeft className="w-5 h-5" /> Volver al Inicio
      </motion.button>
    </motion.div>
  );
}
